import { useState, useCallback } from "react";
import { ImageObj } from "views/uikit/VLCarousel/VLImagesCarouselPreview";
import { CardItem } from "views/components/LabelPropagation/SelectedClassesListStep/ClassCard";
import DeleteIcon from "../../assets/icons/DeleteIcon";

interface UseCarouselForSeedStepPreviewsProps {
  classItemsMap?: Record<string, CardItem[]>;
  onItemDelete: (classId: string, mediaId: string) => void;
  pausedForReview?: boolean;
}

interface UseCarouselForSeedStepPreviewsReturn {
  isCarouselOpen: boolean;
  carouselImages: ImageObj[];
  selectedImageIndex: number;
  currentClassId: string;
  handleImageClick: (items: CardItem[], selectedIndex: number) => void;
  handleCarouselChange: (index: number) => void;
  handleCarouselAction: (result: any) => void;
  closeCarousel: () => void;
}

export const useCarouselForSeedStepPreviews = ({
  classItemsMap,
  onItemDelete,
  pausedForReview = false,
}: UseCarouselForSeedStepPreviewsProps): UseCarouselForSeedStepPreviewsReturn => {
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [carouselImages, setCarouselImages] = useState<ImageObj[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentClassId, setCurrentClassId] = useState<string>("");

  const createDeleteAction = useCallback(
    (mediaId: string, classId: string, index: number) => ({
      type: "default" as const,
      icon: <DeleteIcon />,
      color: "danger" as const,
      tooltip: "Delete this image",
      onClick: () => ({
        type: "delete",
        classId,
        mediaId,
        imageIndex: index,
      }),
    }),
    [],
  );

  const convertToImageObjects = useCallback(
    (items: CardItem[], classId: string): ImageObj[] => {
      return items.map((item, index) => ({
        src: item.imgUri,
        title: item.originUri,
        borderColor: undefined,
        previewBorderColor: undefined,
        opacity: 100,
        actionList: pausedForReview
          ? undefined
          : [
              {
                action: createDeleteAction(item.id, classId, index),
              },
            ],
        floatingDetails: undefined,
        previewFloatingDetails: undefined,
      }));
    },
    [createDeleteAction, pausedForReview],
  );

  const findClassId = useCallback(
    (items: CardItem[]): string => {
      if (!classItemsMap) return "";

      for (const [classId, classItems] of Object.entries(classItemsMap)) {
        if (
          classItems.length === items.length &&
          classItems.every((item, idx) => item.id === items[idx]?.id)
        ) {
          return classId;
        }
      }
      return "";
    },
    [classItemsMap],
  );

  const handleImageClick = useCallback(
    (items: CardItem[], selectedIndex: number) => {
      const foundClassId = findClassId(items);
      const imageObjects = convertToImageObjects(items, foundClassId);

      setCarouselImages(imageObjects);
      setSelectedImageIndex(selectedIndex);
      setCurrentClassId(foundClassId);
      setIsCarouselOpen(true);
    },
    [findClassId, convertToImageObjects],
  );

  const handleCarouselChange = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  const regenerateActionList = useCallback(
    (images: ImageObj[], classId: string): ImageObj[] => {
      return images.map((image, newIndex) => {
        const currentItems = classItemsMap?.[classId] || [];
        const foundItem = currentItems.find(
          (item) => item.imgUri === image.src,
        );

        return {
          ...image,
          actionList: pausedForReview
            ? undefined
            : [
                {
                  action: createDeleteAction(
                    foundItem?.id || "",
                    classId,
                    newIndex,
                  ),
                },
              ],
        };
      });
    },
    [classItemsMap, createDeleteAction, pausedForReview],
  );

  const handleCarouselAction = useCallback(
    (result: any) => {
      if (result.res?.type === "delete") {
        const { classId, mediaId, imageIndex } = result.res;

        onItemDelete(classId, mediaId);

        setCarouselImages((prevImages) => {
          const newImages = [...prevImages];
          newImages.splice(imageIndex, 1);

          if (newImages.length === 0) {
            setIsCarouselOpen(false);
            return [];
          }

          const updatedImages = regenerateActionList(newImages, currentClassId);

          setSelectedImageIndex((prevIndex) => {
            if (prevIndex >= updatedImages.length) {
              return Math.max(0, updatedImages.length - 1);
            }
            return prevIndex;
          });

          return updatedImages;
        });
      }
    },
    [onItemDelete, currentClassId, regenerateActionList],
  );

  const closeCarousel = useCallback(() => {
    setIsCarouselOpen(false);
  }, []);

  return {
    isCarouselOpen,
    carouselImages,
    selectedImageIndex,
    currentClassId,
    handleImageClick,
    handleCarouselChange,
    handleCarouselAction,
    closeCarousel,
  };
};
